import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { AgeBracket, ChecklistTaskData, Region } from '../checklistData';
import { colors } from '../../../../theme';
import ChecklistLessonCard from './ChecklistCards';
import { track } from '../../analytics';

interface ChecklistTaskRowProps {
  task: ChecklistTaskData;
  checked: boolean;
  expanded: boolean;
  onToggleCheck: () => void;
  onToggleExpand: () => void;
  ageBracket: AgeBracket;
  region: Region;
  registerRef: (el: HTMLDivElement | null) => void;
}

function ChecklistTaskRow({
  task, checked, expanded, onToggleCheck, onToggleExpand, ageBracket, region, registerRef,
}: ChecklistTaskRowProps) {
  // Region-gated partner cards (24Pet: US+CA, insurance: US) drop out individually;
  // the task row itself still shows.
  const visibleCards = task.cards.filter((card) => !card.regions || card.regions.includes(region));
  const showCards = visibleCards.length > 0;
  const hasDetail = task.guidance.length > 0 || showCards;

  const handleToggleExpand = () => {
    track('checklist_task_toggle', { task_name: task.title });
    onToggleExpand();
  };

  return (
    <div ref={registerRef}>
      <Box
        onClick={hasDetail ? handleToggleExpand : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: { xs: 1.75, md: '15px' },
          px: { xs: 0.25, md: 0.5 },
          cursor: hasDetail ? 'pointer' : 'default',
        }}
      >
        <ButtonBase
          aria-label={checked ? 'Mark task incomplete' : 'Mark task complete'}
          onClick={(e) => {
            e.stopPropagation();
            onToggleCheck();
          }}
          sx={{
            flexShrink: 0,
            width: 22,
            height: 22,
            borderRadius: '5px',
            border: checked ? 'none' : `2px solid ${colors.primary[400]}`,
            bgcolor: checked ? colors.primary.main : '#fff',
            transition: 'all 150ms',
          }}
        >
          {checked && <CheckIcon sx={{ fontSize: 16, color: '#fff' }} />}
        </ButtonBase>
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: 500,
            fontSize: { xs: 14.5, md: 15.5 },
            lineHeight: 1.3,
            color: checked ? 'text.secondary' : 'text.primary',
          }}
        >
          {task.title}
        </Typography>
        {hasDetail && (
          expanded
            ? <ExpandLessIcon sx={{ flexShrink: 0, fontSize: 22, color: 'text.secondary' }} />
            : <ExpandMoreIcon sx={{ flexShrink: 0, fontSize: 22, color: 'text.secondary' }} />
        )}
      </Box>
      {expanded && hasDetail && (
        <Box
          sx={{
            pl: 1,
            pr: { xs: 0.25, md: 0.5 },
            pb: { xs: 2, md: 2.25 },
            display: 'flex',
            flexDirection: 'column',
            gap: 1.75,
          }}
        >
          {task.guidance.length > 0 && (
            <Box
              component="ul"
              sx={{
                m: 0,
                mt: 0.25,
                pl: 2.25,
              }}
            >
              {task.guidance.map((g) => (
                <Typography
                  key={g}
                  component="li"
                  sx={{
                    fontSize: 13.5,
                    color: 'text.primary',
                    lineHeight: 1.45,
                    '&:not(:last-of-type)': { mb: 0.75 },
                  }}
                >
                  {g}
                </Typography>
              ))}
            </Box>
          )}
          {showCards && visibleCards.map((card) => (
            <ChecklistLessonCard key={card.title} card={card} ageBracket={ageBracket} taskId={task.id} />
          ))}
        </Box>
      )}
    </div>
  );
}

export default ChecklistTaskRow;
